import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SolicitudService } from '@services/solicitud.service';
import { DocumentoService } from '@services/documento.service';
import { EvaluacionMedicaService } from '@services/evaluacion-medica.service';
import { ComisionMedicaService } from '@services/comision-medica.service';
import { SessionService } from '@services/session.service';
import { AlertService } from '@services/alert.service';
import { ExpedienteDetalle, InstitucionDetalle, DocumentoExpediente, EvaluacionMedicaRequest } from '@models/evaluacion-medica.interface';
import { ComisionMedicaRequest } from '@models/comision-medica.interface';
import ExpedienteEstados from '@shared/enums/expediente-estados.enum';
import Roles from '@shared/enums/roles.enum';

interface TrazabilidadEvento {
  fecha: string; // ISO
  usuario: string;
  tipo: string; // e.g., 'notificacion', 'cambio_estado'
  descripcion: string;
}

@Component({
  selector: 'app-caso-dictamen',
  templateUrl: './caso-dictamen.component.html',
  styleUrls: ['./caso-dictamen.component.scss']
})
export class CasoDictamenComponent implements OnInit {
  @Input() caseId?: string;

  public id?: string | null;
  public hasDictamen = false;
  public isReadOnly = false;
  public dictamen: any = null;
  public documentos: Array<{ name: string; url?: string | undefined; type: 'pdf' | 'zip' }> = [];
  private readonly PLACEHOLDER_PDF = 'https://s2.q4cdn.com/175719177/files/doc_presentations/Placeholder-PDF.pdf';
  public form: FormGroup;
  
  public eventos: TrazabilidadEvento[] = [];
  public isLoading = false;
  public showResolucionForm = false;
  public resolucionForm: FormGroup;

  public expediente: ExpedienteDetalle | null = null;
  public institucion: InstitucionDetalle | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute, 
    private fb: FormBuilder, 
    private solicitudService: SolicitudService, 
    private documentoService: DocumentoService,
    private evaluacionMedicaService: EvaluacionMedicaService,
    private comisionMedicaService: ComisionMedicaService,
    private sessionService: SessionService,
    private alertService: AlertService
  ) {
    this.form = this.fb.group({
      decision: ['', Validators.required],
      observaciones: ['']
    });
    this.resolucionForm = this.fb.group({
      res_resultado: ['', Validators.required],
      res_folio: [''],
      firma: [''],
      run: ['']
    });
  }

  get canEmitirResolucion(): boolean {
    const roles = this.sessionService.getRoles() as number[];
    return roles.some(r => [Roles.medico_contralor, Roles.comision_medica, Roles.miembro_junta, Roles.gestion_compin].includes(r));
  }

  get resolucionDisabled(): boolean {
    const blockedStates = [
      ExpedienteEstados.resolucion_emitida,
      ExpedienteEstados.en_apelacion,
      ExpedienteEstados.cierre_administrativo,
      ExpedienteEstados.rechazado
    ];
    return !this.expediente || blockedStates.includes(this.expediente.eex_codigo);
  }

  ngOnInit(): void {
    this.id = this.caseId ?? this.route.snapshot.paramMap.get('id');
    if (this.id) {
      this.loadForId(+this.id);
    }
  }

  private async loadForId(id: number) {
    this.isLoading = true;
    try {
      const [evaluacionData, comisionData, apelaciones, expedienteCompleto] = await Promise.all([
        this.solicitudService.obtenerDetalleEvalucionMedica(id),
        this.solicitudService.obtenerDetalleComisionMedica(id),
        this.solicitudService.obtenerApelaciones(id),
        this.solicitudService.obtenerExpedienteDetalle(id)
      ]);

      const events: TrazabilidadEvento[] = [];
      const docs: Array<{ name: string; url?: string | undefined; type: 'pdf' | 'zip' }> = [];

      if (expedienteCompleto) {
        this.expediente = expedienteCompleto.expediente;
        this.institucion = expedienteCompleto.institucion as unknown as InstitucionDetalle;
        
        // Determine if read-only based on state
        // Specifically for 'Resolución emitida' (7) we might want read-only
        if (this.expediente.eex_codigo === ExpedienteEstados.resolucion_emitida || this.sessionService.isFuncionarioEvaluado()) {
            this.isReadOnly = true;
        }

        if (expedienteCompleto.documentos && expedienteCompleto.documentos.length > 0) {
            docs.push(...expedienteCompleto.documentos.map((d: DocumentoExpediente) => ({
                name: d.doc_nombre_archivo,
                url: d.doc_url_segura,
                type: d.doc_formato.toLowerCase() as 'pdf' | 'zip'
            })));
        }
      } else if (evaluacionData) {
         this.expediente = this.expediente || evaluacionData.expediente;
         this.institucion = this.institucion || evaluacionData.institucion;
      }

      if (evaluacionData) {
        if (evaluacionData.evaluacion_existente) {
          const ev = evaluacionData.evaluacion_existente;
          events.push({
            fecha: ev.emc_fecha,
            usuario: 'Médico Evaluador',
            tipo: 'Evaluación Médica',
            descripcion: `Dictamen: ${ev.emc_dictamen}`
          });
          this.hasDictamen = true;
          this.dictamen = {
            fecha: ev.emc_fecha,
            medico: 'Médico Evaluador',
            decision: ev.emc_dictamen,
            criterios: 'ver detalle', 
            recomendaciones: 'ver detalle'
          };
          this.form.patchValue({ decision: ev.emc_dictamen });
        }
      }

      if (comisionData.comision_existente) {
        const cm = comisionData.comision_existente;
        events.push({
          fecha: cm.cme_fecha_sesion,
          usuario: 'Comisión Médica',
          tipo: 'Comisión Médica',
          descripcion: `Dictamen: ${cm.cme_dictamen}`
        });
      }

      if (comisionData.evaluacion_medico_contralor) {
        const mc = comisionData.evaluacion_medico_contralor;
        const descParts = [`Dictamen: ${mc.emc_dictamen || ''}`];
        if (mc.emc_observaciones) {
          descParts.push(`Observaciones: ${mc.emc_observaciones}`);
        }
        
        events.push({
          fecha: mc.emc_fecha,
          usuario: `${mc.usu_nombres || ''} ${mc.usu_apellido_paterno || ''}`.trim(),
          tipo: 'Evaluación Contralor',
          descripcion: descParts.join('. ')
        });
      }

      if (apelaciones && apelaciones.length > 0) {
        apelaciones.forEach(ape => {
          const descParts = [];
          if (ape.ape_motivo) descParts.push(`Motivo: ${ape.ape_motivo}`);
          if (ape.eap_nombre) descParts.push(`Estado: ${ape.eap_nombre}`);
          
          events.push({
            fecha: ape.ape_fecha,
            usuario: 'Solicitante',
            tipo: 'Apelación',
            descripcion: descParts.join('. ')
          });
        });
      }

      this.eventos = events.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());

      this.documentos = docs.length > 0 ? docs : [];

    } catch (error) {
      console.error('Error loading case data', error);
      this.hasDictamen = false;
    } finally {
      this.isLoading = false;
    }
  }

  async viewDocumento(doc: { name: string; url?: string | undefined; type: 'pdf' | 'zip' }) {
    if (!doc.url) {
        window.open(this.PLACEHOLDER_PDF, '_blank');
        return;
    }
    
    if (doc.url === this.PLACEHOLDER_PDF) {
         window.open(doc.url, '_blank');
         return;
    }

    try {
        const blob = await this.documentoService.verDocumento(doc.url);
        const objectUrl = window.URL.createObjectURL(blob);
        
        if (doc.type === 'pdf') {
            window.open(objectUrl, '_blank');
        } else {
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = doc.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
        
    } catch (error) {
        this.alertService.error('Error al visualizar el documento');
    }
  }

  async emitirResolucion() {
    if (!this.expediente) return;
    
    const payload = {
      exp_id: this.expediente.exp_id
    };

    this.isLoading = true;
    this.alertService.showLoading('Cargando resolución...');
    try {
      const response: any = await this.solicitudService.emitirResolucion(payload);
      this.alertService.close();

      if (response?.data?.pdf_url) {
        window.open(response.data.pdf_url, '_blank');
      }
      if (response?.data?.pdf_generado === false) {
        this.alertService.warning('La resolución fue registrada, pero no se pudo generar el PDF.');
      }
      if (response?.data?.notificacion_enviada === false) {
        this.alertService.warning('La resolución fue registrada, pero no se pudo enviar la notificación al funcionario.');
      }

      if (!response?.data?.pdf_generado && !response?.data?.notificacion_enviada) {
        this.alertService.success('Resolución emitida exitosamente.');
      }

      this.expediente!.eex_codigo = ExpedienteEstados.resolucion_emitida;
      this.isReadOnly = true;
      this.showResolucionForm = false;
    } catch (error: any) {
      this.alertService.close();
      if (error?.status === 409) {
        this.alertService.error('Ya existe una resolución para este expediente.');
      } else {
        this.alertService.error('Error al emitir la resolución. Por favor intente nuevamente.');
      }
    } finally {
      this.isLoading = false;
    }
  }

  async saveDecision() {
    if (this.isReadOnly) {
        return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    
    if (!this.expediente) {
        this.alertService.warning('No se ha cargado la información del expediente.');
        return;
    }

    const val = this.form.value;
    const sesion = this.sessionService.getSesionActual();
    const rutUsuario = sesion ? parseInt(sesion.rut_usuario.replace(/\D/g, ''), 10) : 1;
    
    this.isLoading = true;
    this.alertService.showLoading('Guardando decisión...');

    try {
        const estado = this.expediente.eex_codigo;

        if (estado === ExpedienteEstados.pendiente_evaluacion_medica || estado === ExpedienteEstados.en_apelacion) {
            const payload: EvaluacionMedicaRequest = {
                exp_id: this.expediente.exp_id,
                emc_dictamen: val.decision,
                emc_observaciones: val.observaciones,
                usu_rut: rutUsuario.toString()
            };
            await this.evaluacionMedicaService.registrarEvaluacion(payload);
            this.alertService.success('Evaluación médica registrada exitosamente');
        } else if (estado === ExpedienteEstados.en_comision_medica) {
            const payload: ComisionMedicaRequest = {
                exp_id: this.expediente.exp_id,
                cme_dictamen: val.decision,
                cme_observaciones: val.observaciones,
                usu_rut: rutUsuario.toString()
            };
            await this.comisionMedicaService.registrarSesion(payload);
            this.alertService.success('Sesión de comisión médica registrada exitosamente');
        } else {
            console.warn('Estado no soportado para registrar decisión desde esta vista:', estado);
            this.alertService.warning('No se puede registrar una decisión para el estado actual del expediente.');
        }

        if (this.id) {
            this.router.navigate(['/administracion/dictamen']);
        }

    } catch (error) {
        console.error('Error al guardar dictamen', error);
        this.alertService.error('Ocurrió un error al guardar el dictamen.');
    } finally {
        this.alertService.close();
        this.isLoading = false;
    }
  }
}
